import { call, all, put, takeLatest, delay } from 'redux-saga/effects';

import history from '~/services/history';
import notification from '~/services/notification';
import dailyRouteService from './daily-route-service';

import { BRAZILIAN_STATES, ISA_ERROR, USER_ROLE } from '~/constants';
import { formatMessage } from '~/i18n/translate';
import * as dailyRouteActions from './daily-route-actions';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM,
  GET_ALL_SELECTS_REQUEST
} from './daily-route-constants';
import userService from '~/services/modules/user/user-service';

export function* getAllDailyRoute({ payload }: any) {
  try {
    yield put(dailyRouteActions.setLoadingData(true));

    const [{ data }, { data: count }] = yield all([
      call(dailyRouteService.getAllDailyRoute, {
        userExternalCodes: payload?.sellers?.map(seller => seller.externalCode)?.join(',') ?? ''
      }),
      call(dailyRouteService.getAllDailyRoute, {
        isCount: true,
        userExternalCodes: payload?.sellers?.map(seller => seller.externalCode)?.join(',') ?? ''
      })
    ]);

    if (data) {
      yield put(
        dailyRouteActions.getAllSuccess({
          rows: data,
          rowsCount: count?.count || 0
        })
      );
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(dailyRouteActions.setLoadingData(false));
  }
}

export function* getById({ id }: any) {
  try {
    yield put(dailyRouteActions.setLoadingData(true));

    const { data: users } = yield call(userService.getUsers);

    if (!id) {
      yield put(
        dailyRouteActions.getByIdSuccess({
          selects: {
            users: users
              .filter(user => user.userRole === USER_ROLE.VENDOR || user.userRole === USER_ROLE.TELEMARKETING)
              .map((user: any) => ({
                name: user.name ?? '',
                externalCode: user.externalCode ?? ''
              }))
          }
        })
      );
    } else {
      const { data } = yield call(dailyRouteService.getDailyVisit, id);

      const parsedData = {
        ...data,
        selectCitiesForUser: data?.exclusiveCities?.length > 0 ? true : false,
        state:
          data?.exclusiveCities?.length > 0
            ? BRAZILIAN_STATES.find(state => state.externalCode === data?.exclusiveCities[0]?.state)
            : null,
        exclusiveCities: data?.exclusiveCities?.map((city: { cityCode: string; city: string; state: string }) => ({
          externalCode: city.cityCode,
          name: city.city,
          state: BRAZILIAN_STATES.find(state => state.externalCode === city.state).externalCode
        })),
        visitCycle:
          data?.visitCycle?.map(
            (cycle: {
              startPoint: { lat: number; lon: number };
              beginDate: string;
              endDate: string;
              exclusiveCities: { city: string; state: string; cityCode: string }[];
            }) => ({
              startPoint: {
                lat: cycle.startPoint.lat,
                lon: cycle.startPoint.lon
              },
              beginDate: cycle.beginDate,
              endDate: cycle.endDate,
              exclusiveCities: cycle.exclusiveCities.map((city: { city: string; state: string; cityCode: string }) => ({
                city: city.city,
                state: city.state,
                cityCode: city.cityCode
              }))
            })
          ) || []
      };

      if (data) {
        yield put(
          dailyRouteActions.getByIdSuccess({
            data: parsedData,
            selects: {
              users: users
                .filter(user => user.userRole === USER_ROLE.VENDOR || user.userRole === USER_ROLE.TELEMARKETING)
                .map((user: any) => ({
                  name: user.name ?? '',
                  externalCode: user.externalCode ?? ''
                }))
            }
          })
        );
      }
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(dailyRouteActions.setLoadingData(false));
  }
}

export function* save(data: any) {
  try {
    const { id } = data;
    yield put(dailyRouteActions.setLoadingSend(true));

    const parsedPayload = {
      userExternalCode: data.payload.user?.externalCode,
      userName: data.payload.user?.name,
      beginDate: data.payload.beginDate ? new Date(data.payload.beginDate).toISOString() : new Date().toISOString(),
      latitude: data.payload.position?.latitude,
      longitude: data.payload.position?.longitude,
      address: data.payload?.address || '',
      exclusiveCities:
        data.payload?.exclusiveCities?.map((city: { name: string; externalCode: string }) => ({
          city: city.name,
          state: data?.payload?.state.externalCode,
          cityCode: city.externalCode
        })) || [],
      visitCycle: data.payload?.visitCycle || []
    };

    if (id) {
      yield call(dailyRouteService.editDailyVisit, { _id: id, ...parsedPayload });
    } else {
      yield call(dailyRouteService.createDailyVisit, parsedPayload);
    }

    yield call(history.push, `/register/daily-route/`, {
      date: new Date().getTime()
    });

    yield call(
      notification.success,
      id ? formatMessage('dailyRouteFormUpdatedSuccess') : formatMessage('dailyRouteFormCreatedSuccess')
    );
  } catch (error: any) {
    if (
      error?.response?.data?.message === 'Route config already exists for this user' &&
      error?.response?.status === 409
    ) {
      yield call(notification.error, formatMessage('dailyRouteFormErrorAlreadyExists'));
    } else {
      yield put({ type: ISA_ERROR, error });
    }
  } finally {
    yield put(dailyRouteActions.setLoadingSend(false));
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(dailyRouteActions.setLoadingSend(true));
      yield call(dailyRouteService.deleteDailyVisit, id);
      yield put(dailyRouteActions.setLoadingSend(false));

      yield put(dailyRouteActions.getAllRequest());
      yield call(notification.success, formatMessage('dailyRouteFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(dailyRouteActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/daily-route/edit/${id}`);
}

export function* getAllSelects({}: any) {
  yield delay(200);

  try {
    yield put(dailyRouteActions.setLoadingSelects(true));

    const { data: sellers } = yield call(dailyRouteService.getSellers);

    yield put(
      dailyRouteActions.getAllSelectsSuccess({
        selects: {
          sellers: sellers?.filter(seller => seller?.userRole === USER_ROLE?.VENDOR)
        }
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
    yield put(dailyRouteActions.setLoadingSelects(false));
  } finally {
    yield put(dailyRouteActions.setLoadingSelects(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAllDailyRoute),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(GET_ALL_SELECTS_REQUEST, getAllSelects)
]);
